// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type { CmtsComplaintDetailVm, CmtsComplaintRecordVm } from "../../view-models/complaintViewModels.js";
import type { CmtsConfigurationDraftVm, CmtsConfigurationVersionVm } from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import { decideCmtsExport, createCmtsExportAuditPayload } from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import { normalizeTypedReportCode, reportCodeFromPathname } from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import { activeRows, assertRepositoryOk, businessValueOptionOf, complaintQuery, createOrUpdateMasterData, dataOr, deactivateMasterData, diagnosticsOf, emptyBackendDashboard, errorOf, invalidateAdminArea, loadMasterOptions, masterCrudDisplayName, optionOf, reactivateMasterData, tenantId, useAdminDependencyData, useCmtsRouterSearch, type CmtsConnectedContainerProps } from "../domainContainerSupport.js";
import { CmtsFieldSchemaAdminPage } from "@modules/pages/cmtsFieldSchemaAdminPage.js";
import { CmtsOrganizationAdminPage } from "@modules/pages/cmtsOrganizationAdminPage.js";
import { CmtsRoutingAdminPage } from "@modules/pages/cmtsRoutingAdminPage.js";
import { CmtsConfigurationGovernancePage } from "@modules/pages/cmtsConfigurationGovernancePage.js";
import { CmtsAdminEvidencePage } from "@modules/pages/cmtsAdminEvidencePage.js";
import { CmtsAdminNotificationsPage } from "@modules/pages/cmtsAdminNotificationsPage.js";
import { CmtsSystemOperationsPage } from "@modules/pages/cmtsSystemOperationsPage.js";
import { CmtsAccessGovernancePage } from "@modules/pages/cmtsAccessGovernancePage.js";
import { runtimeErrorMessage } from "../runtimeContainerErrors.js";


export function CmtsAccessGovernanceContainer({ runtime, permissionContext }: CmtsConnectedContainerProps) {
  const t = tenantId(runtime);
  const session = readCmtsSession();
  const effectiveAccess = useQuery({ queryKey: ["cmts", "access", "effective", t], queryFn: () => runtime.repositories.access.effective(t), retry: 1 });
  const userPermissions = useQuery({ queryKey: ["cmts", "access", "permissions", session?.userId, t], queryFn: () => runtime.repositories.access.userPermissions(String(session?.userId ?? ""), t), enabled: Boolean(session?.userId), retry: 1 });
  return <CmtsAccessGovernancePage diagnostics={diagnosticsOf(runtime.diagnostics, effectiveAccess.data, userPermissions.data)} permissionContext={permissionContext} effectiveAccess={effectiveAccess.data?.ok ? effectiveAccess.data.data : undefined} userPermissions={userPermissions.data?.ok ? userPermissions.data.data : []} />;
}

export function CmtsAdminEvidenceContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "admin", "evidence-policies", t];
  const query = useQuery({ queryKey: key, queryFn: () => runtime.repositories.masterData.list("evidencePolicies", t), retry: 1 });
  const saveMutation = useMutation({ mutationFn: (payload: Record<string, unknown>) => createOrUpdateMasterData(runtime, "evidencePolicies", payload, t, masterCrudDisplayName(payload)), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const deactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => deactivateMasterData(runtime, "evidencePolicies", row, "Dependency checked deactivation from evidence policy administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const reactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => reactivateMasterData(runtime, "evidencePolicies", row, "Dependency checked reactivation from evidence policy administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  return <CmtsAdminEvidencePage items={dataOr(query.data, []) as readonly Record<string, unknown>[]} diagnostics={diagnosticsOf(runtime.diagnostics, query.data, saveMutation.data, deactivate.data, reactivate.data)} dependencyData={dependencyData} onUpsert={async (payload) => { assertRepositoryOk(await saveMutation.mutateAsync(payload), "Administration save"); }} onDeactivate={async (row) => { assertRepositoryOk(await deactivate.mutateAsync(row), "Administration deactivate"); }} onReactivate={async (row) => { assertRepositoryOk(await reactivate.mutateAsync(row), "Administration reactivate"); }} onTest={async (row) => { await runtime.repositories.masterDependencies.checkReadiness({ tenantId: t, masterType: "evidencePolicies", entityId: String(row.id ?? row.code ?? ""), operation: "evidence-policy-readiness-test" }); }} onRefresh={async () => { await query.refetch(); }} />;
}

export function CmtsAdminNotificationsContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "admin", "notification-templates", t];
  const query = useQuery({ queryKey: key, queryFn: () => runtime.repositories.masterData.list("notificationTemplates", t), retry: 1 });
  const saveMutation = useMutation({ mutationFn: (payload: Record<string, unknown>) => createOrUpdateMasterData(runtime, "notificationTemplates", payload, t, masterCrudDisplayName(payload)), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const deactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => deactivateMasterData(runtime, "notificationTemplates", row, "Dependency checked deactivation from notification template administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const reactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => reactivateMasterData(runtime, "notificationTemplates", row, "Dependency checked reactivation from notification template administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  return <CmtsAdminNotificationsPage />;
}

export function CmtsConfigurationGovernanceContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const draftsKey = ["cmts", "config", "drafts", t];
  const versionsKey = ["cmts", "config", "versions", t];
  const drafts = useQuery({ queryKey: draftsKey, queryFn: () => runtime.repositories.configurationGovernance.drafts(t), staleTime: 60_000, retry: false, throwOnError: false });
  const versions = useQuery({ queryKey: versionsKey, queryFn: () => runtime.repositories.configurationGovernance.versions(t), staleTime: 60_000, retry: false, throwOnError: false });
  const configurationTypeOptions = useQuery({ queryKey: ["cmts", "master-options", "configuration-type", t], queryFn: () => loadMasterOptions(runtime, "configuration-type", t, "configuration-type"), staleTime: 60_000, retry: false, throwOnError: false });
  const activationModeOptions = useQuery({ queryKey: ["cmts", "master-options", "activation-mode", t], queryFn: () => loadMasterOptions(runtime, "activation-mode", t, "activation-mode"), staleTime: 60_000, retry: false, throwOnError: false });
  const refreshConfigurationGovernance = async () => {
    await queryClient.invalidateQueries({ queryKey: draftsKey });
    await queryClient.invalidateQueries({ queryKey: versionsKey });
    await queryClient.invalidateQueries({ queryKey: ["cmts", "admin"] });
    await queryClient.invalidateQueries({ queryKey: ["cmts", "dashboard"] });
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      let changeObj: Record<string, unknown> = {};
      try {
        changeObj = typeof payload.changePayload === "string" ? JSON.parse(payload.changePayload) : (payload.changePayload as Record<string, unknown> ?? {});
      } catch {
        changeObj = {};
      }

      const masters = (changeObj && typeof changeObj === "object" && "masters" in changeObj && typeof (changeObj as any).masters === "object")
        ? (changeObj as any).masters
        : {
          routingRules: [],
          sodPolicies: []
        };

      const payloadEnvelope = {
        schemaVersion: "cmts-enterprise-maturity-v1",
        tenantId: t,
        metadata: {
          draftId: payload.draftId,
          title: payload.title ?? payload.displayName ?? payload.draftId,
          configurationType: payload.configurationType ?? "Routing Matrix",
          reviewer: payload.reviewer,
          approver: payload.approver,
          activationMode: payload.activationMode,
          scheduledAt: payload.scheduledAt,
          timestamp: new Date().toISOString()
        },
        masters
      };

      const importRequest = {
        tenantId: t,
        payloadJson: JSON.stringify(payloadEnvelope),
        sourceEnvironmentCode: "DEV",
        importReason: String(payload.changeReason ?? payload.reason ?? "Configuration governance draft creation"),
        validateOnly: false
      };

      return await runtime.repositories.configurationGovernance.importDraft(importRequest);
    },
    onSuccess: refreshConfigurationGovernance
  });

  const action = useMutation({
    mutationFn: async ({ row, actionName }: { row: Record<string, unknown>; actionName: string }) => {
      const id = String(row.draftId ?? row.id ?? row.code ?? row.draftCode ?? row.versionId ?? "");
      const reason = `${actionName} from governance control surface`;
      if (actionName === "validate") return runtime.repositories.configurationGovernance.validateDraft(id, { tenantId: t, reason });
      if (actionName === "dependency-check") return runtime.repositories.configurationGovernance.dependencyCheck(id, { tenantId: t, reason });
      if (actionName === "submit") return runtime.repositories.configurationGovernance.submitDraft(id, { tenantId: t, reason });
      if (actionName === "approve") return runtime.repositories.configurationGovernance.approveDraft(id, { tenantId: t, reason });
      if (actionName === "reject") return runtime.repositories.configurationGovernance.rejectDraft(id, { tenantId: t, reason });
      if (actionName === "schedule") return runtime.repositories.configurationGovernance.scheduleDraft(id, { tenantId: t, reason, scheduledAt: new Date(Date.now() + 86_400_000).toISOString() });
      if (actionName === "activate") return runtime.repositories.configurationGovernance.activateDraft(id, { tenantId: t, reason });
      if (actionName === "rollback") return runtime.repositories.configurationGovernance.rollbackVersion(String(row.versionId ?? row.id ?? id), { tenantId: t, reason });
      return { ok: true, data: undefined, diagnostics: [] };
    },
    onSuccess: refreshConfigurationGovernance
  });

  const draftList = drafts.data?.ok ? (drafts.data.data as readonly CmtsConfigurationDraftVm[]) : [];
  const versionList = versions.data?.ok ? (versions.data.data as readonly CmtsConfigurationVersionVm[]) : [];

  return (
    <CmtsConfigurationGovernancePage
      drafts={draftList}
      versions={versionList}
      masterOptions={{ configurationType: configurationTypeOptions.data ?? [], activationMode: activationModeOptions.data ?? [] }}
      diagnostics={diagnosticsOf(runtime.diagnostics, drafts.data, versions.data, saveMutation.data, action.data)}
      onUpsert={async (payload) => {
        const res = await saveMutation.mutateAsync(payload);
        assertRepositoryOk(res, "Configuration draft save");
      }}
      onAction={async (row, actionName) => {
        await action.mutateAsync({ row, actionName });
      }}
      onRefresh={refreshConfigurationGovernance}
    />
  );
}

export function CmtsFieldSchemaAdminContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "field-schemas", t];
  const query = useQuery({ queryKey: key, queryFn: () => runtime.repositories.masterData.list("fieldSchemas", t), retry: 1 });
  const referenceFieldSchemas = useQuery({ queryKey: ["cmts", "reference", "fieldSchemas", t], queryFn: () => runtime.repositories.reference.fieldSchemas(t), retry: 1 });
  const saveMutation = useMutation({ mutationFn: (payload: Record<string, unknown>) => createOrUpdateMasterData(runtime, "fieldSchemas", payload, t, masterCrudDisplayName(payload, payload.schema)), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const deactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => deactivateMasterData(runtime, "fieldSchemas", row, "Dependency checked deactivation from field schema administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const reactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => reactivateMasterData(runtime, "fieldSchemas", row, "Dependency checked reactivation from field schema administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const preview = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.masterDependencies.checkReadiness({ tenantId: t, masterType: "fieldSchemas", entityId: String(row.id ?? row.code ?? ""), operation: "field-schema-preview-readiness" }) });
  return <CmtsFieldSchemaAdminPage rows={[...(dataOr(query.data, []) as readonly Record<string, unknown>[]), ...(dataOr(referenceFieldSchemas.data, []) as readonly Record<string, unknown>[])]} diagnostics={diagnosticsOf(runtime.diagnostics, query.data, referenceFieldSchemas.data, saveMutation.data, deactivate.data, reactivate.data, preview.data)} dependencyData={dependencyData} onUpsert={async (payload) => { assertRepositoryOk(await saveMutation.mutateAsync(payload), "Administration save"); }} onDeactivate={async (row) => { assertRepositoryOk(await deactivate.mutateAsync(row), "Administration deactivate"); }} onReactivate={async (row) => { assertRepositoryOk(await reactivate.mutateAsync(row), "Administration reactivate"); }} onPreview={async (row) => { await preview.mutateAsync(row); }} onRefresh={async () => { await Promise.all([query.refetch(), referenceFieldSchemas.refetch()]); }} />;
}

function flattenOrgTree(nodes: readonly Record<string, unknown>[], parent?: Record<string, unknown>, depth = 0): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  for (const node of nodes) {
    const typeCode = String(node.level ?? node.typeCode ?? "");
    const levelLabel = typeCode || (depth === 0 ? "Business Unit" : depth === 1 ? "Site / Plant" : depth === 2 ? "Department" : "Operational Unit");
    const parentCode = String(node.parentCode ?? parent?.code ?? (node.parentId ? String(node.parentId).substring(0, 8) : "None (Root)"));
    const parentId = node.parentId ? String(node.parentId) : "None (Root)";
    const children = Array.isArray(node.children) ? (node.children as readonly Record<string, unknown>[]) : [];

    const row: Record<string, unknown> = {
      ...node,
      id: node.id ?? node.code,
      code: String(node.code ?? ""),
      name: String(node.name ?? node.label ?? ""),
      label: String(node.name ?? node.label ?? ""),
      level: levelLabel,
      parentCode: parentCode,
      parentId: parentId,
      usageCount: children.length,
      status: "Active",
      active: true,
      description: String(node.description ?? `${node.name ?? node.code} - ${levelLabel}`),
    };
    result.push(row);
    if (children.length > 0) {
      result.push(...flattenOrgTree(children, row, depth + 1));
    }
  }
  return result;
}

export function CmtsOrganizationAdminContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "reference", "organizationTree", t];
  const referenceOrganizationTree = useQuery({ queryKey: key, queryFn: () => runtime.repositories.reference.organizationTree(t), retry: 1 });
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => createOrUpdateMasterData(runtime, "organization", payload, t, masterCrudDisplayName(payload)),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const deactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => deactivateMasterData(runtime, "organization", row, "Dependency checked deactivation from organization administration", t),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const reactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => reactivateMasterData(runtime, "organization", row, "Dependency checked reactivation from organization administration", t),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });

  const rawTree = dataOr(referenceOrganizationTree.data, []) as readonly Record<string, unknown>[];
  const rows = useMemo(() => flattenOrgTree(rawTree), [rawTree]);

  return (
    <CmtsOrganizationAdminPage
      rows={rows}
      diagnostics={diagnosticsOf(runtime.diagnostics, referenceOrganizationTree.data, saveMutation.data, deactivate.data, reactivate.data)}
      dependencyData={dependencyData}
      onUpsert={async (payload) => {
        assertRepositoryOk(await saveMutation.mutateAsync(payload), "Organization save");
      }}
      onDeactivate={async (row) => {
        assertRepositoryOk(await deactivate.mutateAsync(row), "Organization deactivate");
      }}
      onReactivate={async (row) => {
        assertRepositoryOk(await reactivate.mutateAsync(row), "Organization reactivate");
      }}
      onRefresh={async () => {
        await referenceOrganizationTree.refetch();
      }}
    />
  );
}

export function CmtsRoutingAdminContainer({ runtime }: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "routing", t];
  const referenceRouting = useQuery({ queryKey: ["cmts", "reference", "routing", t], queryFn: () => runtime.repositories.reference.routing(t), retry: 1 });
  const saveMutation = useMutation({ mutationFn: (payload: Record<string, unknown>) => createOrUpdateMasterData(runtime, "routing", payload, t, masterCrudDisplayName(payload)), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const deactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => deactivateMasterData(runtime, "routing", row, "Dependency checked deactivation from routing administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const reactivate = useMutation({ mutationFn: (row: Record<string, unknown>) => reactivateMasterData(runtime, "routing", row, "Dependency checked reactivation from routing administration", t), onSuccess: async () => invalidateAdminArea(queryClient, t, [key]) });
  const routingRows = (dataOr(referenceRouting.data, []) as readonly Record<string, unknown>[]).map((row) => ({
    ...row,
    ruleName: row.name ?? row.ruleName,
    sourceType: row.sourceType ?? row.sourceName ?? row.sourceTypeName ?? row.sourceTypeId ?? "Customer",
    category: row.category ?? row.categoryName ?? row.categoryId ?? "Product Quality",
    priority: row.priority ?? row.priorityName ?? row.priorityOrder ?? "High",
    condition: row.condition ?? row.conditionExpression ?? (row.description as string) ?? "Default Condition",
    targetQueue: row.targetQueue ?? row.targetQueueName ?? row.fallbackQueueCode ?? row.ownerRoleName ?? "Quality Triage",
    departmentId: row.department ?? row.departmentName ?? row.departmentId ?? "Quality Assurance",
    ownerId: row.owner ?? row.ownerUserName ?? row.ownerName ?? row.ownerRoleId ?? "QA Lead",
    slaPolicy: row.slaPolicy ?? row.slaProfileName ?? "Standard 7-Day SLA",
    escalationPolicy: row.escalationPolicy ?? row.escalationProfileName ?? "Level 1 Escalation",
    workflowProfile: row.workflowProfile ?? row.workflowProfileName ?? row.workflowProfileId ?? "Standard 8D Investigation",
    status: row.isActive === false ? "Inactive" : "Active",
    active: row.isActive !== false,
  }));
  return <CmtsRoutingAdminPage rows={routingRows} diagnostics={diagnosticsOf(runtime.diagnostics, referenceRouting.data, saveMutation.data, deactivate.data, reactivate.data)} dependencyData={dependencyData} onUpsert={async (payload) => { assertRepositoryOk(await saveMutation.mutateAsync(payload), "Administration save"); }} onDeactivate={async (row) => { assertRepositoryOk(await deactivate.mutateAsync(row), "Administration deactivate"); }} onReactivate={async (row) => { assertRepositoryOk(await reactivate.mutateAsync(row), "Administration reactivate"); }} onTest={async (row) => { await runtime.repositories.masterDependencies.checkReadiness({ tenantId: t, masterType: "routing", entityId: String(row.id ?? row.code ?? ""), operation: "routing-rule-readiness-test" }); }} onRefresh={async () => { await referenceRouting.refetch(); }} />;
}

function isSystemCollectionRow(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unwrapSystemCollection(value: unknown, depth = 0): readonly Record<string, unknown>[] {
  if (depth > 6) return [];
  if (Array.isArray(value)) return value.filter(isSystemCollectionRow);
  if (!isSystemCollectionRow(value)) return [];
  if (value.success === false || value.ok === false || value.isSuccess === false) return [];
  const errorCode = String(value.code ?? value.errorCode ?? "").toLowerCase();
  if (errorCode.includes("adapter_failure") || errorCode.includes("not_found") || errorCode.includes("unexpected_error")) return [];
  if (value.error && !value.data && !value.items && !value.rows && !value.records) return [];
  const collectionKeys = ["data", "items", "rows", "records", "values", "health", "healthSignals", "runs", "operationRuns", "failures", "failureCenter", "outbox", "offlineOutbox", "result", "payload"] as const;
  for (const key of collectionKeys) {
    const candidate = value[key];
    if (Array.isArray(candidate)) return candidate.filter(isSystemCollectionRow);
    if (isSystemCollectionRow(candidate)) {
      const nestedRows = unwrapSystemCollection(candidate, depth + 1);
      if (nestedRows.length) return nestedRows;
    }
  }
  if (value.id || value.operationRunId || value.failureId || value.outboxId || value.healthSignalId) return [value];
  return [];
}

async function loadOptionalCollection(loader: () => Promise<unknown>): Promise<readonly Record<string, unknown>[]> {
  try {
    const response = await loader();
    return unwrapSystemCollection(response);
  } catch {
    return [];
  }
}

export function CmtsSystemOperationsContainer({ runtime }: CmtsConnectedContainerProps) {
  const t = tenantId(runtime);
  const queryClient = useQueryClient();
  const health = useQuery({ queryKey: ["cmts", "system-health", t, "v3"], queryFn: () => loadOptionalCollection(() => runtime.repositories.systemOperations.healthSignals(t)), retry: false, throwOnError: false });
  const runs = useQuery({ queryKey: ["cmts", "system-runs", t, "v3"], queryFn: () => loadOptionalCollection(() => runtime.repositories.systemOperations.operationRuns(t)), retry: false, throwOnError: false });
  const failures = useQuery({ queryKey: ["cmts", "system-failures", t, "v3"], queryFn: () => loadOptionalCollection(() => runtime.repositories.systemOperations.failureCenter(t)), retry: false, throwOnError: false });
  const outbox = useQuery({ queryKey: ["cmts", "offline-outbox", t, "v3"], queryFn: () => loadOptionalCollection(() => runtime.repositories.systemOperations.offlineOutbox(t)), retry: false, throwOnError: false });

  async function invalidateSystemOperations() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["cmts", "system-health", t] }),
      queryClient.invalidateQueries({ queryKey: ["cmts", "system-runs", t] }),
      queryClient.invalidateQueries({ queryKey: ["cmts", "system-failures", t] }),
      queryClient.invalidateQueries({ queryKey: ["cmts", "offline-outbox", t] }),
      queryClient.invalidateQueries({ queryKey: ["cmts", "audit", t] }),
    ]);
  }

  const retry = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.systemOperations.retryOperation(String(row.id ?? row.operationRunId ?? row.code ?? ""), { source: "system-operations-page" }, t),
    onSuccess: invalidateSystemOperations,
  });

  const cancel = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.systemOperations.cancelOperation(String(row.id ?? row.operationRunId ?? row.code ?? ""), { reason: "Cancelled from system operations page" }, t),
    onSuccess: invalidateSystemOperations,
  });

  const replay = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.systemOperations.replayOutbox(String(row.id ?? row.outboxId ?? row.code ?? ""), { source: "system-operations-page" }, t),
    onSuccess: invalidateSystemOperations,
  });

  const review = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.systemOperations.markFailureReviewed(String(row.id ?? row.failureId ?? row.operationRunId ?? ""), { status: "Reviewed" }, t),
    onSuccess: invalidateSystemOperations,
  });

  const task = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.systemOperations.createRemediationTask({
        linkedEntityType: row.operationType ?? row.entityType ?? "SYSTEM_OPERATION",
        linkedEntityId: row.operationRunId ?? row.failureId ?? row.id,
        title: `Remediate ${String(row.operationName ?? row.operationType ?? row.entityType ?? "system operation")}`,
      }, t),
    onSuccess: invalidateSystemOperations,
  });

  async function refreshSystemOperations() {
    await Promise.all([health.refetch(), runs.refetch(), failures.refetch(), outbox.refetch()]);
  }

  return (
    <CmtsSystemOperationsPage
      health={health.data ?? []}
      runs={runs.data ?? []}
      failures={failures.data ?? []}
      outbox={outbox.data ?? []}
      diagnostics={diagnosticsOf(runtime.diagnostics, retry.data, cancel.data, replay.data, review.data, task.data)}
      loading={
        health.isLoading || health.isFetching ||
        runs.isLoading || runs.isFetching ||
        failures.isLoading || failures.isFetching ||
        outbox.isLoading || outbox.isFetching ||
        retry.isPending || cancel.isPending || replay.isPending || review.isPending || task.isPending
      }
      error={
        runtimeErrorMessage(retry.error) ??
        runtimeErrorMessage(cancel.error) ??
        runtimeErrorMessage(replay.error) ??
        runtimeErrorMessage(review.error) ??
        runtimeErrorMessage(task.error) ??
        null
      }
      onRefresh={refreshSystemOperations}
      onRetryOperation={async (row) => { await retry.mutateAsync(row); }}
      onCancelOperation={async (row) => { await cancel.mutateAsync(row); }}
      onReplayOutbox={async (row) => { await replay.mutateAsync(row); }}
      onMarkFailureReviewed={async (row) => { await review.mutateAsync(row); }}
      onCreateTask={async (row) => { await task.mutateAsync(row); }}
    />
  );
}

